import { useEffect, useMemo, useState } from 'react';
import { useRows } from '@/hooks/useLive';
import { save, softDelete, upsert } from '@/lib/crud';
import { hours as fmtHours, num, isoDate } from '@/lib/format';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { ConfirmRemove } from '@/components/ui/ConfirmRemove';
import { StatusChip } from '@/components/ui/StatusChip';
import {
  DateTimeInput, NumberInput, SelectInput, TextArea, TextInput,
} from '@/components/ui/Field';
import type { Machine } from '@/types/models';
import { meterName, meterSuffix, unitOf } from '@/lib/meter';
import type { ServiceStatus } from '../Maintenance';

interface ServiceRecord extends Record<string, unknown> {
  id: string;
  machine_id: string;
  service_type: string;
  scheduled_hours: number | null;
  scheduled_date: string | null;
  performed_at: string | null;
  performed_hours: number | null;
  next_service_hours: number | null;
  status: string;
  technician: string | null;
  workshop: string | null;
  downtime_hours: number | null;
  cost: number | null;
  work_done: string | null;
  notes: string | null;
}

const STATUSES = [
  { value: 'planned', label: 'Planned' },
  { value: 'due', label: 'Due' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];


/**
 * Completing a service is the only thing that moves the machine's service
 * window: the database trigger writes last_service_hours and the next due
 * reading from what is captured here. Nobody edits the machine record to
 * keep the board honest.
 */
export function ServiceDrawer({
  record, board, onClose, locked,
}: { record: Partial<ServiceRecord>; board: ServiceStatus[]; onClose: () => void; locked?: string | null }) {
  const isNew = !record.id;
  const machines = useRows<Machine>('machines', 'fleet_no');

  const [v, setV] = useState<Partial<ServiceRecord>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setV({
      service_type: 'Routine service',
      status: 'planned',
      scheduled_date: isoDate(),
      ...record,
    });
  }, [record]);

  const set = (k: string, val: unknown) => setV((d) => ({ ...d, [k]: val }));

  const machine = useMemo(
    () => (machines ?? []).find((m) => m.id === v.machine_id),
    [machines, v.machine_id],
  );
  const mUnit = unitOf(machine);
  const mSfx = meterSuffix(mUnit);
  const mFmt = (x: number | null | undefined) => (mUnit === 'km' ? num(x ?? 0, 0) : fmtHours(x));
  const status = useMemo(
    () => board.find((b) => b.machine_id === v.machine_id),
    [board, v.machine_id],
  );

  const completed = v.status === 'completed';
  const interval = Number(machine?.service_interval_hours ?? 0);

  // What the machine's next service becomes once this one is saved. Shown so
  // the technician sees the consequence before it is committed, not after.
  const rollsTo = completed && v.performed_hours != null
    ? Number(v.next_service_hours ?? Number(v.performed_hours) + interval)
    : null;

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!v.machine_id) e.machine_id = 'Choose the machine';
    if (!v.service_type) e.service_type = 'Describe the service';
    if (completed) {
      if (!v.performed_at) e.performed_at = 'A completed service needs the date it was done';
      if (v.performed_hours == null) {
        e.performed_hours = 'The hour meter at service is what rolls the next one forward';
      }
    }
    if (v.performed_hours != null && Number(v.performed_hours) < 0) {
      e.performed_hours = 'Cannot be negative';
    }
    if (
      v.next_service_hours != null && v.performed_hours != null
      && Number(v.next_service_hours) <= Number(v.performed_hours)
    ) {
      e.next_service_hours = 'The next service must be ahead of this one';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setBusy(true);
    const payload: Record<string, unknown> = { ...v };
    if (record.id) payload.id = record.id;
    // Left blank on a completed service, the interval decides — that is what
    // the trigger would do anyway, but writing it makes the record explicit.
    if (completed && payload.next_service_hours == null && interval > 0) {
      payload.next_service_hours = Number(v.performed_hours) + interval;
    }
    const ok = await save(
      () => upsert('service_records', payload as Record<string, unknown> & { id?: string }),
      completed ? 'Service completed — next service rolled forward' : 'Service saved',
    );
    setBusy(false);
    if (ok) onClose();
  }

  return (
    <Drawer
      open
      locked={locked}
      width={560}
      title={isNew ? 'Log a service' : 'Service record'}
      subtitle={machine ? `${machine.fleet_no}${machine.name ? ` · ${machine.name}` : ''}` : undefined}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <ConfirmRemove
              disabled={busy}
              label="Remove"
              question="Remove this service record?"
              onConfirm={async () => {
                const ok = await save(() => softDelete('service_records', record.id as string), 'Removed');
                // Close only if it went through. A refusal leaves the record
                // open with the reason on screen, rather than closing over it.
                if (ok !== null) onClose();
                return ok !== null;
              }}
            />
          )}
          <span className="spacer" />
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>
            {completed ? 'Complete service' : 'Save'}
          </Button>
        </>
      }
    >
      {status && (
        <div className="issue issue--ok" style={{ marginBottom: 'var(--sp-3)', alignItems: 'center' }}>
          <span>{mUnit === 'km' ? 'Odometer' : 'Meter'} {mFmt(status.current_hour_meter)} {mSfx}</span>
          <span className="spacer" />
          <StatusChip tone={(status.hours_remaining ?? 1) <= 0 ? 'red' : 'grey'}>
            {(status.hours_remaining ?? 1) <= 0
              ? `${mFmt(Math.abs(status.hours_remaining ?? 0))} ${mSfx} overdue`
              : `${mFmt(status.hours_remaining)} ${mSfx} to next service`}
          </StatusChip>
        </div>
      )}

      <div className="form-grid">
        <SelectInput id="sv-machine" label="Machine" required error={errors.machine_id}
          value={(v.machine_id as string) ?? null}
          options={(machines ?? []).filter((m) => m.is_active)
            .map((m) => ({ value: m.id, label: m.fleet_no }))}
          onChange={(x) => set('machine_id', x)} />
        <SelectInput id="sv-status" label="Status" value={(v.status as string) ?? 'planned'}
          options={STATUSES} placeholder="" onChange={(x) => set('status', x ?? 'planned')} />

        <div className="field--full">
          <TextInput id="sv-type" label="Service" required error={errors.service_type}
            value={String(v.service_type ?? '')} onChange={(x) => set('service_type', x)}
            help={interval > 0 ? `Interval on this machine: ${num(interval)} ${mSfx}` : undefined} />
        </div>

        <NumberInput id="sv-sched-h" label={`Due at ${meterName(mUnit)}`} suffix={mSfx} min={0} step={mUnit === 'km' ? 1000 : 50}
          value={(v.scheduled_hours as number) ?? null}
          onChange={(x) => set('scheduled_hours', x)} />
        <TextInput id="sv-sched-d" label="Planned date" type="date"
          value={String(v.scheduled_date ?? '')} onChange={(x) => set('scheduled_date', x || null)} />

        <DateTimeInput id="sv-done" label="Carried out at" error={errors.performed_at}
          required={completed}
          value={(v.performed_at as string) ?? null}
          onChange={(x) => set('performed_at', x)} />
        <NumberInput id="sv-done-h" label={`${mUnit === 'km' ? 'Odometer' : 'Hour meter'} at service`} suffix={mSfx} min={0} step={1}
          required={completed} error={errors.performed_hours}
          value={(v.performed_hours as number) ?? null}
          onChange={(x) => set('performed_hours', x)} />

        <NumberInput id="sv-next" label="Next service due at" suffix={mSfx} min={0} step={mUnit === 'km' ? 1000 : 50}
          error={errors.next_service_hours}
          value={(v.next_service_hours as number) ?? null}
          onChange={(x) => set('next_service_hours', x)}
          help={interval > 0 ? 'Leave blank to add the interval automatically.' : undefined} />
        <NumberInput id="sv-down" label="Downtime" suffix="h" min={0} step={0.5}
          value={(v.downtime_hours as number) ?? null}
          onChange={(x) => set('downtime_hours', x)}
          help="Hours the machine stood for this service." />

        <TextInput id="sv-tech" label="Technician" value={String(v.technician ?? '')}
          onChange={(x) => set('technician', x || null)} />
        <TextInput id="sv-shop" label="Workshop" value={String(v.workshop ?? '')}
          onChange={(x) => set('workshop', x || null)} />

        <NumberInput id="sv-cost" label="Cost" min={0} step={100}
          value={(v.cost as number) ?? null} onChange={(x) => set('cost', x)} />

        <div className="field--full">
          <TextArea id="sv-work" label="Work done" value={String(v.work_done ?? '')}
            onChange={(x) => set('work_done', x || null)} />
        </div>
        <div className="field--full">
          <TextArea id="sv-notes" label="Notes" value={String(v.notes ?? '')}
            onChange={(x) => set('notes', x || null)} />
        </div>
      </div>

      {rollsTo != null && (
        <div className="issue issue--ok" style={{ marginTop: 'var(--sp-3)' }}>
          Saving this closes the window at {fmtHours(v.performed_hours as number)} h and
          sets the next service for {fmtHours(rollsTo)} h.
        </div>
      )}
    </Drawer>
  );
}
